import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, UserX } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export const CloseAccount = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  if (user?.role === "admin") return null;
  const close = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/me/delete", { password });
      setUser(null);
      toast.success(`Account closed. You can reactivate it by signing in before ${new Date(data.purge_after).toLocaleDateString("en-GB")}.`);
      navigate("/", { replace: true });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <section className="mt-14 border-t border-border pt-8" data-testid="close-account-section">
      <h2 className="font-display text-2xl">Close your account</h2>
      <p className="mt-2 max-w-xl text-sm text-muted-foreground">Your profile, photos and messages disappear straight away and experiences you lead are cancelled. Sign in again within 30 days to reactivate; after that everything is deleted for good.</p>
      <button type="button" className="btn-outline mt-4 border-destructive/50 text-destructive hover:border-destructive hover:text-destructive" onClick={() => setOpen(true)} data-testid="close-account-button"><UserX className="h-4 w-4" /> Close my account</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md border-destructive/40 bg-card" data-testid="close-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-destructive">Close your account?</DialogTitle>
            <DialogDescription>Enter your password to confirm. You'll be signed out and can reactivate within 30 days by signing in again.</DialogDescription>
          </DialogHeader>
          <label className="field-label" htmlFor="close-pw">Password</label>
          <input id="close-pw" type="password" autoComplete="current-password" className="field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="close-account-password" />
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-outline" onClick={() => setOpen(false)} data-testid="close-account-cancel">Cancel</button>
            <button type="button" className="btn-primary bg-destructive text-destructive-foreground hover:bg-destructive/90" disabled={!password || busy} onClick={close} data-testid="close-account-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />} Close account
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};
